(() => {
  const MINIMUM_SUBSCRIPTION_QUANTITY = 4;
  const CONTROL_SELECTOR = [
    '#checkout',
    '#CartDrawer-Checkout',
    '#cart-notification-form [name="checkout"]',
  ].join(',');
  const CHECKOUT_FORM_SELECTOR = '#cart, #CartDrawer-Form, #cart-notification-form';
  const CART_QUANTITY_BUTTON_SELECTOR = [
    '#cart .quantity__button',
    '#CartDrawer-Form .quantity__button',
    '#cart-notification-form .quantity__button',
    'cart-remove-button a',
  ].join(',');
  const CART_QUANTITY_INPUT_SELECTOR = [
    '#cart input[name="updates[]"]',
    '#CartDrawer-Form input[name="updates[]"]',
    '#cart-notification-form input[name="updates[]"]',
  ].join(',');
  const MESSAGE_CLASS = 'cj-subscription-minimum-message';
  const MANAGED_CLASS = 'cj-subscription-minimum-control';

  let blocked = false;
  let validationPending = false;
  let validationFailed = false;
  let subscriptionQuantity = 0;
  let refreshSequence = 0;
  let pendingCartRequests = 0;
  let mutationIntentTimer;
  let applyFrame;

  const subscriptionUnits = (cart) =>
    (cart?.items || []).reduce(
      (total, item) => total + (item.selling_plan_allocation ? Number(item.quantity) || 0 : 0),
      0
    );

  const messageContent = () => {
    if (validationFailed) {
      return {
        title: 'We could not verify your cart',
        detail: 'Please check your connection and try again before checking out.',
      };
    }

    const remaining = MINIMUM_SUBSCRIPTION_QUANTITY - subscriptionQuantity;
    return {
      title: `Add ${remaining} more ${remaining === 1 ? 'jar' : 'jars'} to continue`,
      detail: `Subscriptions require at least ${MINIMUM_SUBSCRIPTION_QUANTITY} jars. Your cart currently has ${subscriptionQuantity} subscription ${
        subscriptionQuantity === 1 ? 'jar' : 'jars'
      }.`,
    };
  };

  const controls = () => Array.from(document.querySelectorAll(CONTROL_SELECTOR));
  const restrictCheckout = () => blocked || validationPending || validationFailed;

  const restoreControl = (control) => {
    if (!control.classList.contains(MANAGED_CLASS)) return;

    control.disabled = control.dataset.cjSubscriptionMinimumWasDisabled === 'true';
    if (control.dataset.cjSubscriptionMinimumAriaDisabled === '__missing__') {
      control.removeAttribute('aria-disabled');
    } else {
      control.setAttribute('aria-disabled', control.dataset.cjSubscriptionMinimumAriaDisabled);
    }

    delete control.dataset.cjSubscriptionMinimumWasDisabled;
    delete control.dataset.cjSubscriptionMinimumAriaDisabled;
    control.classList.remove(MANAGED_CLASS);
  };

  const disableControl = (control) => {
    if (!control.classList.contains(MANAGED_CLASS)) {
      control.dataset.cjSubscriptionMinimumWasDisabled = String(Boolean(control.disabled));
      control.dataset.cjSubscriptionMinimumAriaDisabled = control.hasAttribute('aria-disabled')
        ? control.getAttribute('aria-disabled')
        : '__missing__';
    }

    control.disabled = true;
    control.setAttribute('aria-disabled', 'true');
    control.classList.add(MANAGED_CLASS);
  };

  const messageInsertionPoint = (control) => {
    if (control.id === 'checkout') return control.closest('.cart__ctas') || control;
    if (control.id === 'CartDrawer-Checkout') return control.closest('.cart__ctas') || control;
    return control.closest('form') || control;
  };

  const ensureMessage = (control, index) => {
    const insertionPoint = messageInsertionPoint(control);
    const host = insertionPoint.parentElement;
    if (!host) return;

    let message = host.querySelector(`:scope > .${MESSAGE_CLASS}`);
    if (!message) {
      message = document.createElement('div');
      message.className = MESSAGE_CLASS;
      message.id = `cj-subscription-minimum-message-${index}`;
      message.setAttribute('role', 'alert');
      message.setAttribute('aria-live', 'polite');
      message.setAttribute('tabindex', '-1');

      const icon = document.createElement('span');
      icon.className = `${MESSAGE_CLASS}__icon`;
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '!';

      const title = document.createElement('strong');
      title.className = `${MESSAGE_CLASS}__title`;

      const detail = document.createElement('span');
      detail.className = `${MESSAGE_CLASS}__detail`;

      message.append(icon, title, detail);
      host.insertBefore(message, insertionPoint);
    }

    const content = messageContent();
    const title = message.querySelector(`.${MESSAGE_CLASS}__title`);
    const detail = message.querySelector(`.${MESSAGE_CLASS}__detail`);
    if (title && title.textContent !== content.title) title.textContent = content.title;
    if (detail && detail.textContent !== content.detail) detail.textContent = content.detail;
  };

  const applyState = () => {
    applyFrame = undefined;
    const restricted = restrictCheckout();
    document.body.classList.toggle('cj-subscription-minimum-blocked', restricted);
    document.body.dataset.cjSubscriptionQuantity = String(subscriptionQuantity);
    document.body.dataset.cjSubscriptionValidationState = validationPending
      ? 'checking'
      : validationFailed
        ? 'unavailable'
        : blocked
          ? 'blocked'
          : 'allowed';

    const currentControls = controls();
    if (restricted) {
      currentControls.forEach((control, index) => {
        disableControl(control);
        if (blocked || validationFailed) ensureMessage(control, index);
      });
      if (!blocked && !validationFailed) {
        document.querySelectorAll(`.${MESSAGE_CLASS}`).forEach((message) => message.remove());
      }
      return;
    }

    currentControls.forEach(restoreControl);
    document.querySelectorAll(`.${MESSAGE_CLASS}`).forEach((message) => message.remove());
  };

  const scheduleApply = () => {
    if (applyFrame) return;
    applyFrame = window.requestAnimationFrame(applyState);
  };

  const beginValidation = () => {
    validationPending = true;
    validationFailed = false;
    applyState();
  };

  const beginMutationIntent = () => {
    beginValidation();
    window.clearTimeout(mutationIntentTimer);
    mutationIntentTimer = window.setTimeout(() => {
      mutationIntentTimer = undefined;
      if (validationPending && pendingCartRequests === 0) void refresh();
    }, 2000);
  };

  const updateFromCart = (cart) => {
    subscriptionQuantity = subscriptionUnits(cart);
    blocked = subscriptionQuantity > 0 && subscriptionQuantity < MINIMUM_SUBSCRIPTION_QUANTITY;
    validationPending = pendingCartRequests > 0;
    validationFailed = false;
    scheduleApply();
  };

  const cartJsonUrl = () => `${window.routes?.cart_url || '/cart'}.js`;

  const refresh = async () => {
    const sequence = ++refreshSequence;
    try {
      const response = await fetch(cartJsonUrl(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
      const cart = await response.json();
      if (sequence === refreshSequence) updateFromCart(cart);
    } catch (error) {
      if (sequence !== refreshSequence) return;
      validationPending = pendingCartRequests > 0;
      validationFailed = !validationPending && subscriptionQuantity > 0;
      scheduleApply();
      console.warn('Subscription minimum check unavailable.', error);
    }
  };

  const cartMutationRequest = (input) => {
    try {
      const requestUrl = new URL(typeof input === 'string' ? input : input?.url, window.location.href);
      return (
        requestUrl.origin === window.location.origin &&
        /\/cart\/(add|change|update|clear)(?:\.js)?\/?$/.test(requestUrl.pathname)
      );
    } catch (error) {
      return false;
    }
  };

  const beginCartRequest = () => {
    window.clearTimeout(mutationIntentTimer);
    mutationIntentTimer = undefined;
    pendingCartRequests += 1;
    beginValidation();
  };

  const finishCartRequest = () => {
    pendingCartRequests = Math.max(0, pendingCartRequests - 1);
    if (pendingCartRequests === 0) void refresh();
  };

  const nativeFetch = window.fetch.bind(window);
  window.fetch = (...args) => {
    if (!cartMutationRequest(args[0])) return nativeFetch(...args);

    beginCartRequest();
    try {
      return Promise.resolve(nativeFetch(...args)).finally(finishCartRequest);
    } catch (error) {
      finishCartRequest();
      throw error;
    }
  };

  const showBlockingMessage = () => {
    scheduleApply();
    window.requestAnimationFrame(() => {
      const message = document.querySelector(`.${MESSAGE_CLASS}`);
      if (message) {
        message.scrollIntoView({ behavior: 'smooth', block: 'center' });
        message.focus({ preventScroll: true });
      }
    });
  };

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest(CART_QUANTITY_BUTTON_SELECTOR)) beginMutationIntent();
    },
    true
  );

  document.addEventListener(
    'input',
    (event) => {
      if (event.target.matches(CART_QUANTITY_INPUT_SELECTOR)) beginMutationIntent();
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      if (event.target.matches(CART_QUANTITY_INPUT_SELECTOR)) beginMutationIntent();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!restrictCheckout()) return;
      const checkoutTarget = event.target.closest(
        `${CONTROL_SELECTOR}, .additional-checkout-buttons button, .additional-checkout-buttons a`
      );
      if (!checkoutTarget) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showBlockingMessage();
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!restrictCheckout() || !event.target.matches(CHECKOUT_FORM_SELECTOR)) return;
      const submitter = event.submitter;
      if (submitter && submitter.name !== 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showBlockingMessage();
    },
    true
  );

  const observer = new MutationObserver(() => {
    if (restrictCheckout()) scheduleApply();
  });
  observer.observe(document.body, { childList: true, subtree: true });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const cart = event?.cartData;
      if (cart && Array.isArray(cart.items)) {
        refreshSequence += 1;
        updateFromCart(cart);
        return;
      }

      beginValidation();
      void refresh();
    });
  }

  const validateCart = () => {
    beginValidation();
    void refresh();
  };

  window.addEventListener('pageshow', validateCart);
  validateCart();
})();
